import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

const ADVERTISERS_ENDPOINT = "https://paisley.org.uk/api/advertisers";
const LOGO_BASE = "https://paisley.org.uk";

/**
 * Community Partners — fetched live from paisley.org.uk's advertiser API.
 * Renders a horizontally-scrolling row on mobile and a wrapped grid on desktop.
 * Silently hides if the API is unreachable so we never break the marketing page.
 */
export default function CommunityPartners() {
  const [partners, setPartners] = useState([]);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(ADVERTISERS_ENDPOINT)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        // Sort by `order` field, ascending, then alphabetical fallback
        arr.sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.name || "").localeCompare(b.name || ""));
        setPartners(arr);
      })
      .catch((e) => { if (!cancelled) { console.warn("CommunityPartners fetch failed:", e); setErrored(true); } });
    return () => { cancelled = true; };
  }, []);

  if (errored || partners.length === 0) return null;

  return (
    <section
      className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto border-t border-white/5"
      data-testid="community-partners-section"
    >
      <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
        <div className="overline mb-3" data-testid="community-partners-eyebrow">— Our Community Partners</div>
        <p className="text-sm sm:text-base text-white/60 leading-relaxed" data-testid="community-partners-caption">
          InstaVibe is proud to support the Paisley &amp; Glasgow business community.
        </p>
      </div>

      {/* Mobile: horizontal scroll. Desktop: wrapped grid. */}
      <div
        className="flex sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory sm:overflow-visible pb-3 sm:pb-0 -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="community-partners-list"
      >
        {partners.map((p) => (
          <a
            key={p.id}
            href={p.link}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="group shrink-0 w-[150px] sm:w-auto snap-start surface-card p-4 sm:p-5 flex flex-col items-center justify-center gap-3 hover:border-klein/40 hover:bg-white/5 transition-colors aspect-[4/3] sm:aspect-auto sm:min-h-[140px]"
            data-testid={`community-partner-${p.id}`}
            title={`Visit ${p.name}`}
          >
            <div className="flex-1 w-full flex items-center justify-center min-h-0">
              <img
                src={`${LOGO_BASE}${p.logo}`}
                alt={p.name}
                loading="lazy"
                className="max-w-full max-h-12 sm:max-h-14 object-contain opacity-80 group-hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="text-[10px] sm:text-xs text-white/55 group-hover:text-cyan transition-colors text-center inline-flex items-center gap-1 leading-tight">
              <span className="truncate max-w-[110px]">{p.name}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
